console.log("keyboard-customizer-dialog.js loading...");

import { DialogComponent } from "@theme/dialog";
import { KEYBOARD_COLORS, KNOB_COLORS } from './keyboard-colors.js';

console.log(
  "keyboard-customizer-dialog.js: DialogComponent imported successfully"
);

/**
 * Keyboard configurations for different keyboard types
 */
const KEYBOARD_CONFIGS = {
  corne: {
    id: 'corne',
    name: 'Corne',
    keyCount: 42,
    keysPerSide: 21,
    hasKnobs: false,
    mirrorMap: {
      L1: 'R6', L2: 'R5', L3: 'R4', L4: 'R3', L5: 'R2', L6: 'R1',
      L7: 'R12', L8: 'R11', L9: 'R10', L10: 'R9', L11: 'R8', L12: 'R7',
      L13: 'R18', L14: 'R17', L15: 'R16', L16: 'R15', L17: 'R14', L18: 'R13',
      L19: 'R21', L20: 'R20', L21: 'R19'
    }
  },
  sofle: {
    id: 'sofle',
    name: 'Sofle',
    keyCount: 58,
    keysPerSide: 29,
    hasKnobs: true,
    knobElements: ['left', 'right'],
    mirrorMap: {
      L1: 'R6', L2: 'R5', L3: 'R4', L4: 'R3', L5: 'R2', L6: 'R1',
      L7: 'R12', L8: 'R11', L9: 'R10', L10: 'R9', L11: 'R8', L12: 'R7',
      L13: 'R18', L14: 'R17', L15: 'R16', L16: 'R15', L17: 'R14', L18: 'R13',
      L19: 'R24', L20: 'R23', L21: 'R22', L22: 'R21', L23: 'R20', L24: 'R19',
      L25: 'R29', L26: 'R28', L27: 'R27', L28: 'R26', L29: 'R25'
    }
  }
};

/**
 * Keyboard Customizer Dialog Component
 * Extends DialogComponent to provide keyboard color customization functionality
 */
export class KeyboardCustomizerDialog extends DialogComponent {
  requiredRefs = ["dialog"];

  #currentComponentId = null;
  #colorData = null;
  #mainColors = [];
  #knobColors = [];
  #keyColors = {};
  #leftTopCaseColor = "";
  #leftBottomCaseColor = "";
  #rightTopCaseColor = "";
  #rightBottomCaseColor = "";
  #knobColor = "";
  #selectedPaintColor = null;
  #keyboardType = 'corne';
  #keyboardConfig = null;

  connectedCallback() {
    super.connectedCallback();
    console.log("KeyboardCustomizerDialog connected");
    this.#initializeSwatchListeners();
    this.#initializeActionButtons();
  }

  /**
   * Opens the dialog with provided data
   * @param {string} componentId - Unique identifier for the calling component
   * @param {Array} mainColors - Main color palette for keys and cases
   * @param {Array} knobColors - Knob color palette for encoder knobs
   * @param {Object} keyColors - Current key color assignments
   * @param {Object} settings - Settings including defaults
   */
  open(componentId, mainColors, knobColors, keyColors, settings) {
    console.log("Dialog open() called", {
      componentId,
      mainColors,
      knobColors,
      keyColors,
      settings,
    });
    this.#currentComponentId = componentId;
    this.#mainColors = mainColors;
    this.#knobColors = knobColors;
    this.#colorData = mainColors; // For backward compatibility with existing methods
    this.#keyColors = { ...keyColors };

    // Initialize keyboard type and config
    this.#keyboardType = settings.keyboardType || 'corne';
    this.#keyboardConfig = KEYBOARD_CONFIGS[this.#keyboardType];

    if (!this.#keyboardConfig) {
      console.error(`Unknown keyboard type: ${this.#keyboardType}, falling back to corne`);
      this.#keyboardType = 'corne';
      this.#keyboardConfig = KEYBOARD_CONFIGS.corne;
    }

    console.log(`Dialog initialized for keyboard: ${this.#keyboardType}`, this.#keyboardConfig);

    this.#leftTopCaseColor = settings.leftTopCaseColor || settings.topCaseColor || settings.defaultTopCode || "";
    this.#leftBottomCaseColor = settings.leftBottomCaseColor || settings.bottomCaseColor || settings.defaultBottomCode || "";
    this.#rightTopCaseColor = settings.rightTopCaseColor || settings.topCaseColor || settings.defaultTopCode || "";
    this.#rightBottomCaseColor = settings.rightBottomCaseColor || settings.bottomCaseColor || settings.defaultBottomCode || "";
    this.#knobColor = settings.knobColor || "";

    // Only apply default colors to keys that don't already have colors assigned
    const defaultColor = this.#colorData[0]?.code;
    if (defaultColor) {
      for (let i = 1; i <= this.#keyboardConfig.keysPerSide; i++) {
        const leftKey = `L${i}`;
        const rightKey = `R${i}`;
        // Only set default color if no custom color exists for this key
        if (!this.#keyColors[leftKey]) {
          this.#keyColors[leftKey] = defaultColor;
        }
        if (!this.#keyColors[rightKey]) {
          this.#keyColors[rightKey] = defaultColor;
        }
      }
    }

    // Initialize knob color if keyboard has knobs
    if (this.#keyboardConfig.hasKnobs && !this.#knobColor) {
      this.#knobColor = this.#knobColors[0]?.code || "";
    }

    // Show/hide knob section based on keyboard type
    const knobSection = this.querySelector('[data-knob-section]');
    if (knobSection) {
      knobSection.style.display = this.#keyboardConfig.hasKnobs ? 'block' : 'none';
    }

    // Auto-select first color for immediate painting
    if (this.#colorData.length > 0) {
      this.#selectedPaintColor = this.#colorData[0];
      this.#updateSwatchSelection();
    }

    this.#populateSwatches();
    this.#updatePreview();
    this.#updateSelectedLabels();

    console.log("Calling showDialog()");
    this.showDialog();
  }

  /**
   * Update swatch selection visually
   */
  #updateSwatchSelection() {
    if (!this.#selectedPaintColor) return;

    const paintContainer = this.querySelector('[data-palette="paint"]');
    if (paintContainer) {
      paintContainer
        .querySelectorAll(".kb-customizer__swatch")
        .forEach((s) => s.classList.remove("is-active"));
      const activeSwatch = paintContainer.querySelector(
        `[data-code="${this.#selectedPaintColor.code}"]`
      );
      if (activeSwatch) {
        activeSwatch.classList.add("is-active");
      }
    }
  }

  /**
   * Initialize swatch click listeners using event delegation
   */
  #initializeSwatchListeners() {
    // Paint color swatches
    const paintContainer = this.querySelector('[data-palette="paint"]');
    if (paintContainer) {
      paintContainer.addEventListener("click", (e) => {
        const swatch = e.target.closest(".kb-customizer__swatch");
        if (swatch) {
          this.#handleSwatchClick(swatch, "paint");
        }
      });
    }

    // Left top case swatches
    const leftTopContainer = this.querySelector('[data-palette="left-top"]');
    if (leftTopContainer) {
      leftTopContainer.addEventListener("click", (e) => {
        const swatch = e.target.closest(".kb-customizer__swatch");
        if (swatch) {
          this.#handleSwatchClick(swatch, "left-top");
        }
      });
    }

    // Left bottom case swatches
    const leftBottomContainer = this.querySelector('[data-palette="left-bottom"]');
    if (leftBottomContainer) {
      leftBottomContainer.addEventListener("click", (e) => {
        const swatch = e.target.closest(".kb-customizer__swatch");
        if (swatch) {
          this.#handleSwatchClick(swatch, "left-bottom");
        }
      });
    }

    // Right top case swatches
    const rightTopContainer = this.querySelector('[data-palette="right-top"]');
    if (rightTopContainer) {
      rightTopContainer.addEventListener("click", (e) => {
        const swatch = e.target.closest(".kb-customizer__swatch");
        if (swatch) {
          this.#handleSwatchClick(swatch, "right-top");
        }
      });
    }

    // Right bottom case swatches
    const rightBottomContainer = this.querySelector('[data-palette="right-bottom"]');
    if (rightBottomContainer) {
      rightBottomContainer.addEventListener("click", (e) => {
        const swatch = e.target.closest(".kb-customizer__swatch");
        if (swatch) {
          this.#handleSwatchClick(swatch, "right-bottom");
        }
      });
    }

    // Knob color swatches
    const knobContainer = this.querySelector('[data-palette="knob"]');
    if (knobContainer) {
      knobContainer.addEventListener("click", (e) => {
        const swatch = e.target.closest(".kb-customizer__swatch");
        if (swatch) {
          this.#handleSwatchClick(swatch, "knob");
        }
      });
    }

    // Key click listeners for per-key painting
    this.addEventListener("click", (e) => {
      const key = e.target.closest("[data-key]");
      if (key && this.#selectedPaintColor) {
        this.#paintKey(key);
      }
    });
  }

  /**
   * Initialize action button listeners
   */
  #initializeActionButtons() {
    const mirrorLTRBtn = this.querySelector('[data-action="mirror-ltr"]');
    if (mirrorLTRBtn) {
      mirrorLTRBtn.addEventListener("click", () => this.#mirrorLeftToRight());
    }

    const mirrorRTLBtn = this.querySelector('[data-action="mirror-rtl"]');
    if (mirrorRTLBtn) {
      mirrorRTLBtn.addEventListener("click", () => this.#mirrorRightToLeft());
    }

    const fillBtn = this.querySelector('[data-action="fill"]');
    if (fillBtn) {
      fillBtn.addEventListener("click", () => this.#fillAll());
    }

    const resetBtn = this.querySelector('[data-action="reset"]');
    if (resetBtn) {
      resetBtn.addEventListener("click", () => this.#reset());
    }
  }

  /**
   * Handle swatch click
   */
  #handleSwatchClick(swatch, paletteType) {
    const code = swatch.dataset.code;
    const name = swatch.dataset.name;
    const hex = swatch.dataset.hex;

    // Remove active class from siblings
    const container = swatch.parentElement;
    container
      .querySelectorAll(".kb-customizer__swatch")
      .forEach((s) => s.classList.remove("is-active"));

    // Add active class to clicked swatch
    swatch.classList.add("is-active");

    if (paletteType === "paint") {
      this.#selectedPaintColor = { code, name, hex };
      this.#updateSelectedLabels();
    } else {
      // Handle case color and knob palettes
      switch (paletteType) {
        case "left-top":
          this.#leftTopCaseColor = code;
          break;
        case "left-bottom":
          this.#leftBottomCaseColor = code;
          break;
        case "right-top":
          this.#rightTopCaseColor = code;
          break;
        case "right-bottom":
          this.#rightBottomCaseColor = code;
          break;
        case "knob":
          this.#knobColor = code;
          break;
      }
      this.#updatePreview();
      this.#updateSelectedLabels();
    }
  }

  /**
   * Paint a single key
   */
  #paintKey(keyElement) {
    if (!this.#selectedPaintColor) return;

    const keyId = keyElement.dataset.key;
    this.#keyColors[keyId] = this.#selectedPaintColor.code;

    // Update visual
    keyElement.style.fill = this.#selectedPaintColor.hex;

    // Mark as modified (for future features)
    keyElement.classList.add("is-modified");
  }

  /**
   * Mirror left side colors to right side
   */
  #mirrorLeftToRight() {
    const mirrorMap = this.#getKeyMirrorMap();
    const defaultColor = this.#colorData[0]?.code;

    for (const [leftKey, rightKey] of Object.entries(mirrorMap)) {
      if (this.#keyColors[leftKey]) {
        this.#keyColors[rightKey] = this.#keyColors[leftKey];
      } else {
        // If left key has no color, reset right key to default (first available color)
        delete this.#keyColors[rightKey];
      }
    }

    this.#updatePreview();
  }

  /**
   * Mirror right side colors to left side
   */
  #mirrorRightToLeft() {
    const mirrorMap = this.#getKeyMirrorMap();
    const defaultColor = this.#colorData[0]?.code;
    console.log("Mirroring right to left", this.#keyColors);
    for (const [leftKey, rightKey] of Object.entries(mirrorMap)) {
      if (this.#keyColors[rightKey]) {
        console.log("Mirroring", rightKey, "to", leftKey);
        this.#keyColors[leftKey] = this.#keyColors[rightKey];
      } else {
        // If right key has no color, reset left key to default (first available color)
        delete this.#keyColors[leftKey];
      }
    }

    this.#updatePreview();
  }

  /**
   * Fill all keys with selected paint color
   */
  #fillAll() {
    if (!this.#selectedPaintColor) return;

    // Fill all keys based on keyboard config
    for (let i = 1; i <= this.#keyboardConfig.keysPerSide; i++) {
      this.#keyColors[`L${i}`] = this.#selectedPaintColor.code;
      this.#keyColors[`R${i}`] = this.#selectedPaintColor.code;
    }

    this.#updatePreview();
  }

  /**
   * Reset to default colors
   */
  #reset() {
    // Clear all custom colors and reapply default prefilling
    this.#keyColors = {};
    const defaultColor = this.#colorData[0]?.code;
    if (defaultColor) {
      for (let i = 1; i <= this.#keyboardConfig.keysPerSide; i++) {
        this.#keyColors[`L${i}`] = defaultColor;
        this.#keyColors[`R${i}`] = defaultColor;
      }
    }

    // Reset case colors to defaults from settings
    this.#leftTopCaseColor = "";
    this.#leftBottomCaseColor = "";
    this.#rightTopCaseColor = "";
    this.#rightBottomCaseColor = "";

    // Reset knob color if keyboard has knobs
    if (this.#keyboardConfig.hasKnobs) {
      this.#knobColor = this.#knobColors[0]?.code || "";
    }

    this.#updatePreview();
    this.#updateSelectedLabels();
  }

  /**
   * Get mirror mapping for left-right keys
   */
  #getKeyMirrorMap() {
    return this.#keyboardConfig.mirrorMap;
  }

  /**
   * Populate color swatches from data
   */
  #populateSwatches() {
    // Paint color swatches - use main colors
    const paintContainer = this.querySelector('[data-palette="paint"]');
    if (paintContainer) {
      paintContainer.innerHTML = "";
      this.#mainColors.forEach((color) => {
        const swatch = this.#createSwatchElement(color, false);
        paintContainer.appendChild(swatch);
      });
    }

    // Left top case swatches - use main colors
    const leftTopSwatchesContainer = this.querySelector(
      '[data-palette="left-top"] .kb-customizer__swatches'
    );
    if (leftTopSwatchesContainer) {
      leftTopSwatchesContainer.innerHTML = "";
      this.#mainColors.forEach((color) => {
        const swatch = this.#createSwatchElement(color, true);
        if (color.code === this.#leftTopCaseColor) {
          swatch.classList.add("is-active");
        }
        leftTopSwatchesContainer.appendChild(swatch);
      });
    }

    // Left bottom case swatches - use main colors
    const leftBottomSwatchesContainer = this.querySelector(
      '[data-palette="left-bottom"] .kb-customizer__swatches'
    );
    if (leftBottomSwatchesContainer) {
      leftBottomSwatchesContainer.innerHTML = "";
      this.#mainColors.forEach((color) => {
        const swatch = this.#createSwatchElement(color, true);
        if (color.code === this.#leftBottomCaseColor) {
          swatch.classList.add("is-active");
        }
        leftBottomSwatchesContainer.appendChild(swatch);
      });
    }

    // Right top case swatches - use main colors
    const rightTopSwatchesContainer = this.querySelector(
      '[data-palette="right-top"] .kb-customizer__swatches'
    );
    if (rightTopSwatchesContainer) {
      rightTopSwatchesContainer.innerHTML = "";
      this.#mainColors.forEach((color) => {
        const swatch = this.#createSwatchElement(color, true);
        if (color.code === this.#rightTopCaseColor) {
          swatch.classList.add("is-active");
        }
        rightTopSwatchesContainer.appendChild(swatch);
      });
    }

    // Right bottom case swatches - use main colors
    const rightBottomSwatchesContainer = this.querySelector(
      '[data-palette="right-bottom"] .kb-customizer__swatches'
    );
    if (rightBottomSwatchesContainer) {
      rightBottomSwatchesContainer.innerHTML = "";
      this.#mainColors.forEach((color) => {
        const swatch = this.#createSwatchElement(color, true);
        if (color.code === this.#rightBottomCaseColor) {
          swatch.classList.add("is-active");
        }
        rightBottomSwatchesContainer.appendChild(swatch);
      });
    }

    // Knob color swatches (if keyboard has knobs) - use separate knob palette
    if (this.#keyboardConfig.hasKnobs) {
      const knobSwatchesContainer = this.querySelector('[data-palette="knob"]');
      if (knobSwatchesContainer) {
        knobSwatchesContainer.innerHTML = "";
        this.#knobColors.forEach((color) => {
          const swatch = this.#createSwatchElement(color, true);
          if (color.code === this.#knobColor) {
            swatch.classList.add("is-active");
          }
          knobSwatchesContainer.appendChild(swatch);
        });
      }
    }
  }

  /**
   * Calculate relative luminance of a color using WCAG 2.0 formula
   * @param {string} hex - Hex color value (#RRGGBB or #RRGGBBAA)
   * @returns {number} Luminance value between 0 (darkest) and 1 (lightest)
   */
  #calculateLuminance(hex) {
    // Remove # and extract RGB (ignore alpha channel)
    const hexValue = hex.replace('#', '').substring(0, 6);

    // Parse RGB components
    const r = parseInt(hexValue.substring(0, 2), 16) / 255;
    const g = parseInt(hexValue.substring(2, 4), 16) / 255;
    const b = parseInt(hexValue.substring(4, 6), 16) / 255;

    // Apply sRGB gamma correction
    const linearize = (component) => {
      return component <= 0.03928
        ? component / 12.92
        : Math.pow((component + 0.055) / 1.055, 2.4);
    };

    const rLinear = linearize(r);
    const gLinear = linearize(g);
    const bLinear = linearize(b);

    // Calculate relative luminance (weighted for human perception)
    // Green is weighted highest because humans are most sensitive to it
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
  }

  /**
   * Create a swatch button element
   * @param {Object} color - Color object with code, name, hex
   * @param {boolean} small - Use small size variant
   * @returns {HTMLElement} Button element
   */
  #createSwatchElement(color, small = false) {
    const button = document.createElement("button");
    button.type = "button";

    // Determine if color is light (for check mark contrast)
    const isLight = this.#calculateLuminance(color.hex) > 0.5;

    // Build class list
    const classes = ["kb-customizer__swatch"];
    if (small) classes.push("kb-customizer__swatch--sm");
    if (isLight) classes.push("is-light");
    else classes.push("is-dark");
    button.className = classes.join(" ");

    // Set attributes
    button.style.background = color.hex;
    button.dataset.code = color.code;
    button.dataset.name = color.name;
    button.dataset.hex = color.hex;
    button.title = color.name;

    // Add checkmark SVG
    button.innerHTML = `
      <svg class="kb-customizer__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    `;

    return button;
  }

  /**
   * Update SVG preview with current colors
   */
  #updatePreview() {
    // Update all keys based on current color assignments
    const keys = this.querySelectorAll("[data-key]");
    keys.forEach((key) => {
      const keyId = key.dataset.key;
      if (this.#keyColors[keyId]) {
        const color = this.#findColorByCode(this.#keyColors[keyId]);
        if (color) {
          key.style.fill = color.hex;
        }
      } else {
        // Ensure keys have first available color fill for better visibility
        if (!key.style.fill || key.style.fill === "none") {
          key.style.fill = this.#colorData[0]?.hex || "white";
        }
      }
    });

    // Update left top case
    const leftTopElements = this.querySelectorAll('[data-case="left"]');
    if (this.#leftTopCaseColor) {
      const leftTopColor = this.#findColorByCode(this.#leftTopCaseColor);
      if (leftTopColor) {
        leftTopElements.forEach((el) => (el.style.fill = leftTopColor.hex));
      }
    }

    // Update left bottom case
    const leftBottomElements = this.querySelectorAll('[data-case="left-bottom"]');
    if (this.#leftBottomCaseColor) {
      const leftBottomColor = this.#findColorByCode(this.#leftBottomCaseColor);
      if (leftBottomColor) {
        leftBottomElements.forEach((el) => (el.style.fill = leftBottomColor.hex));
      }
    }

    // Update right top case
    const rightTopElements = this.querySelectorAll('[data-case="right"]');
    if (this.#rightTopCaseColor) {
      const rightTopColor = this.#findColorByCode(this.#rightTopCaseColor);
      if (rightTopColor) {
        rightTopElements.forEach((el) => (el.style.fill = rightTopColor.hex));
      }
    }

    // Update right bottom case
    const rightBottomElements = this.querySelectorAll('[data-case="right-bottom"]');
    if (this.#rightBottomCaseColor) {
      const rightBottomColor = this.#findColorByCode(this.#rightBottomCaseColor);
      if (rightBottomColor) {
        rightBottomElements.forEach((el) => (el.style.fill = rightBottomColor.hex));
      }
    }

    // Update knobs (if keyboard has knobs)
    if (this.#keyboardConfig.hasKnobs && this.#knobColor) {
      const knobColor = this.#findColorByCode(this.#knobColor);
      if (knobColor) {
        this.#keyboardConfig.knobElements.forEach(side => {
          const knob = this.querySelector(`[data-knob="${side}"]`);
          if (knob) {
            knob.style.fill = knobColor.hex;
          }
        });
      }
    }
  }

  /**
   * Find color object by code from either palette
   */
  #findColorByCode(code) {
    if (!code) return null;

    // Search main colors first
    let color = this.#mainColors.find((c) => c.code === code);

    // If not found, search knob colors
    if (!color) {
      color = this.#knobColors.find((c) => c.code === code);
    }

    return color;
  }

  /**
   * Update selected color labels
   */
  #updateSelectedLabels() {
    const paintLabel = this.querySelector("[data-selected-paint]");
    if (paintLabel && this.#selectedPaintColor) {
      paintLabel.textContent = this.#selectedPaintColor.name;
    }

    const leftTopLabel = this.querySelector("[data-selected-left-top]");
    if (leftTopLabel && this.#leftTopCaseColor) {
      const color = this.#findColorByCode(this.#leftTopCaseColor);
      if (color) leftTopLabel.textContent = `(${color.name})`;
    }

    const leftBottomLabel = this.querySelector("[data-selected-left-bottom]");
    if (leftBottomLabel && this.#leftBottomCaseColor) {
      const color = this.#findColorByCode(this.#leftBottomCaseColor);
      if (color) leftBottomLabel.textContent = `(${color.name})`;
    }

    const rightTopLabel = this.querySelector("[data-selected-right-top]");
    if (rightTopLabel && this.#rightTopCaseColor) {
      const color = this.#findColorByCode(this.#rightTopCaseColor);
      if (color) rightTopLabel.textContent = `(${color.name})`;
    }

    const rightBottomLabel = this.querySelector("[data-selected-right-bottom]");
    if (rightBottomLabel && this.#rightBottomCaseColor) {
      const color = this.#findColorByCode(this.#rightBottomCaseColor);
      if (color) rightBottomLabel.textContent = `(${color.name})`;
    }

    const knobLabel = this.querySelector("[data-selected-knob]");
    if (knobLabel && this.#keyboardConfig.hasKnobs && this.#knobColor) {
      const color = this.#findColorByCode(this.#knobColor);
      if (color) knobLabel.textContent = `(${color.name})`;
    }
  }

  /**
   * Apply changes and close dialog
   */
  applyChanges() {
    const payload = {
      componentId: this.#currentComponentId,
      keyColors: this.#keyColors,
      leftTopCase: this.#leftTopCaseColor,
      leftBottomCase: this.#leftBottomCaseColor,
      rightTopCase: this.#rightTopCaseColor,
      rightBottomCase: this.#rightBottomCaseColor,
    };

    // Add knob color if keyboard has knobs
    if (this.#keyboardConfig.hasKnobs) {
      payload.knobColor = this.#knobColor;
    }

    const event = new KeyboardCustomizerApplyEvent(payload);

    document.dispatchEvent(event);
    this.closeDialog();
  }
}

/**
 * Custom event for apply action
 */
export class KeyboardCustomizerApplyEvent extends CustomEvent {
  constructor(detail) {
    super(KeyboardCustomizerApplyEvent.eventName, {
      bubbles: true,
      detail,
    });
  }

  static eventName = "keyboard-customizer:apply";
}

if (!customElements.get("keyboard-customizer-dialog")) {
  console.log("Defining keyboard-customizer-dialog");
  customElements.define("keyboard-customizer-dialog", KeyboardCustomizerDialog);
  console.log("keyboard-customizer-dialog defined");
} else {
  console.log("keyboard-customizer-dialog already defined");
}
